import { useEffect, useCallback, useRef } from "react";
import { useLocation, useNavigationType } from "react-router-dom";

export const ScrollToTop = () => {
  const { pathname, hash } = useLocation();
  const navType = useNavigationType();
  const initialLoadHandledRef = useRef(false);

  // Keep re-aligning the target as lazy sections above it mount and shift layout.
  // We watch the document for ~3s and re-call scrollIntoView whenever the target's
  // top position changes, which corrects the drift caused by progressively-loaded
  // sibling sections (e.g. ProcessSection / LeadershipSection above #team).
  const scrollToHash = useCallback((targetHash: string) => {
    const startTime = performance.now();
    const maxDuration = 6000; // ms — generous window to outlast lazy chunk loads
    let lastTop = Number.NaN;
    let stableFrames = 0;
    const stableFramesNeeded = 60; // ~1s of no movement before we trust the position
    let everFound = false;

    const tick = () => {
      const el = document.querySelector(targetHash);
      const elapsed = performance.now() - startTime;
      if (el) {
        everFound = true;
        const top = (el as HTMLElement).getBoundingClientRect().top + window.scrollY;
        if (top !== lastTop) {
          el.scrollIntoView({ behavior: "instant" });
          lastTop = top;
          stableFrames = 0;
        } else {
          stableFrames++;
        }
      } else if (everFound) {
        // Element vanished after appearing (unlikely, but reset stability so we re-align if it comes back)
        stableFrames = 0;
        lastTop = Number.NaN;
      }
      if (elapsed < maxDuration && stableFrames < stableFramesNeeded) {
        requestAnimationFrame(tick);
      }
    };
    requestAnimationFrame(tick);
  }, []);

  // Initial-load: if the page was opened directly on a hash URL (e.g. /#services
  // from a bookmark, share link, or full-page reload), wait for the lazy section
  // to mount and scroll to it. Runs only once.
  useEffect(() => {
    if (initialLoadHandledRef.current) return;
    initialLoadHandledRef.current = true;
    const initialHash = window.location.hash;
    if (!initialHash) return;
    scrollToHash(initialHash);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (navType === "POP") return;
    if (hash) {
      scrollToHash(hash);
    } else {
      window.scrollTo({ top: 0, left: 0, behavior: "instant" });
    }
  }, [pathname, hash, navType, scrollToHash]);

  // Fallback: listen for hashchange events that React Router may not capture
  useEffect(() => {
    const handleHashChange = () => {
      const newHash = window.location.hash;
      if (newHash) {
        scrollToHash(newHash);
      }
    };
    window.addEventListener("hashchange", handleHashChange);
    return () => window.removeEventListener("hashchange", handleHashChange);
  }, [scrollToHash]);

  return null;
};
